/* eslint-disable @typescript-eslint/no-explicit-any */
import moment, { Moment } from "moment";
import { CreateUpdateGoalsPayload, Goals, Quarters } from "./newipmsmodel";
import { GoalsFlagStatusEnum } from "./constant";
import { format } from "date-fns";

interface GroupedGoals {
  [quarter: number]: string[];
}

export const convertGoalsToGroupedIds = (goals: Goals[]): GroupedGoals => {
  return goals.reduce<GroupedGoals>((acc, goal) => {
    if (goal?.id !== null) {
      // Initialize the array for this quarter if it doesn't exist
      const quarterNum = goal.quater || 0;
      if (!acc[quarterNum]) {
        acc[quarterNum] = [];
      }

      // Ensure the goal ID is unique within the quarter
      if (!acc[quarterNum].includes(goal.id)) {
        acc[quarterNum].push(goal.id);
      }
    }

    return acc;
  }, {} as GroupedGoals);
};

const quarterMapping: Record<string, number> = {
  q1: 1,
  q2: 2,
  q3: 3,
  q4: 4,
};

export const convertSaveUpdateGoalsToPayload = (
  isNew: boolean,
  softGoalsData: Goals[],
  orgGoalsData: Goals[],
  submitstatus: number,
  startYear: number,
  employeeidData: number,
  data: Quarters,
  deletedQuarters: Goals[],
  assessmentStart: string,
  assessmentEnd: string,
  onlyQuarters: string,
  approver_id: string,
  // rejectFunctionFlag: boolean
): CreateUpdateGoalsPayload => {
  const isSubmit = submitstatus === GoalsFlagStatusEnum.submit;
  const payload: CreateUpdateGoalsPayload = {
    quarters_data: {
      insert: [],
      update: [],
      delete: [],
    },
    start_date: assessmentStart,
    // end_date: assessmentEnd,
    approver_id: Number(approver_id),
    employee_id: employeeidData,
    is_submit: isSubmit,
    isNew: isNew,
  };

  // Process deletions
  payload.quarters_data.delete = Array.from(
    new Set(
      deletedQuarters
        .filter((g) => g.id !== null)
        .map((g) => String(g.id))
    )
  );

  const cleanGoalForPayload = (goal: Goals): Omit<
    Goals,
    | "changesType"
    | "goal_status"
    | "employeesubmitted"
    | "employeeid"
    | "current"
    | "startyear"
  > => {
    const cleaned = { ...goal };
    delete cleaned.changesType;
    delete cleaned.goal_status;
    delete cleaned.employeesubmitted;
    delete cleaned.employeeid;
    delete (cleaned as any).current;
    delete (cleaned as any).startyear;
    return cleaned as any;
  };

  const processGoal = (goal: Goals, quarterNumber: number, category: string) => {
    const processedGoal = {
      ...goal,
      quater: quarterNumber,
      category: category,
    };

    // If it is isNew (full assessment) or id is null or explicitly marked as new
    const isInsert = isNew || goal.id === null || goal.changesType === "new";

    const cleaned = cleanGoalForPayload(processedGoal) as any;

    if (isInsert) {
      payload.quarters_data.insert.push(cleaned);
    } else {
      payload.quarters_data.update.push(cleaned);
    }
  };

  // Determine which quarters to process for KRA goals
  const quartersToProcess = onlyQuarters ? [onlyQuarters] : Object.keys(data);

  quartersToProcess.forEach((quarter) => {
    if (Object.prototype.hasOwnProperty.call(data, quarter)) {
      const quarterNumber = quarterMapping[quarter];
      const quarterDataEntries = data[quarter];
      if (!quarterDataEntries) return;

      // Process only KRA goals from the quarterly data state
      if (Array.isArray(quarterDataEntries.kra)) {
        quarterDataEntries.kra.forEach((goal) => {
          processGoal(goal, quarterNumber, "kra");
        });
      }
    }
  });

  // Process Soft goals (usually associated with Q1)
  // if (Array.isArray(softGoalsData)) {
  //   softGoalsData.forEach((goal) => {
  //     processGoal({ ...goal, target_date: assessmentEnd, weightage: 100, goal_description: '' }, 1, "soft");
  //   });
  // }

  // // Process Org goals (usually associated with Q1)
  // if (Array.isArray(orgGoalsData)) {
  //   orgGoalsData.forEach((goal) => {
  //     processGoal({ ...goal, target_date: assessmentEnd, weightage: 100, goal_description: '' }, 1, "org");
  //   });
  // }

  return payload;
};

export type DateRange = {
  start: Moment | null;
  end: Moment | null;
};

export const getQuarterRanges = (startDate: Moment, endDate: Moment): DateRange[] => {
  const quarterRanges: DateRange[] = [];
  let currentStartDate = startDate.clone();

  while (currentStartDate.isBefore(endDate)) {
    let currentEndDate = currentStartDate.clone().add(3, "months");

    if (currentEndDate.isAfter(endDate)) {
      currentEndDate = endDate.clone();
    }

    quarterRanges.push({ start: currentStartDate, end: currentEndDate });
    currentStartDate = currentEndDate.clone();
  }

  return quarterRanges;
};

// Function to format the month and year as "mmm yyyy"
const formatMonthYear = (date: Date): { label: string; value: string } => {
  const value = moment(date).format("YYYY-MM-DD");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return { label: `${month} ${year}`, value: value };
};

// Function to generate an array of formatted month names for a date range
export const generateMonthArray = (startDate: Date, endDate: Date): { label: string; value: string }[] => {
  const monthArray: { label: string; value: string }[] = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    monthArray.push(formatMonthYear(currentDate));

    // Manually update the month
    const nextMonth = currentDate.getMonth() + 1;
    currentDate = new Date(currentDate.getFullYear(), nextMonth, 1);
  }
  return monthArray;
};

export const validateTime = (time: string, date: string): string | null => {
  const [hours, minutes] = time.split(":").map(Number);
  const isTimeInvalid = hours > 24 || (hours === 24 && minutes > 0);

  if (isTimeInvalid) {
    return `Total number of hours on ${date} should not be greater than 24:00 hours.`;
  }

  return null;
};

// report healper
//
export const formatAsSevenDigitNumber = (id: number) => {
  return id ? id.toString().padStart(7, "0") : "";
};

export const getSameDateThisYear = (dateString: string, year: number): string => {
  const [day, monthName] = dateString.split(" ");
  const month = new Date(Date.parse(monthName + " 1, 2020")).getMonth(); // Get month index

  // Create a new date object with the provided year
  const newDate = new Date(year, month, parseInt(day, 10));

  // Format the new date as 'dd MMMM yyyy'
  return format(newDate, "yyyy-MM-dd");
};

export const generateWeeksInMonth = (selectedDate: Date, userID: number) => {
  const startOfMonth = moment(selectedDate).startOf("month");
  const endOfMonth = moment(selectedDate).endOf("month");

  const weeks = [];
  const currentWeekStart = startOfMonth.clone().startOf("week");

  while (currentWeekStart.isBefore(endOfMonth)) {
    const weekStart = currentWeekStart.clone();
    const weekEnd = currentWeekStart.clone().endOf("week");

    weeks.push({
      start: weekStart.toDate(),
      end: weekEnd.toDate(),
      userId: userID,
    });

    currentWeekStart.add(1, "week");
  }

  return weeks;
};


